import { api } from '../services/authService';

export const S3_BROWSER_BLOCK_ERROR = 'S3_BROWSER_DIRECT_UPLOAD_FAILED';

/** @returns {Promise<{ post_url: string, form_fields: object, s3_path: string }>} */
export async function postUploadPresign(apiPath, file, organization, headers) {
  const formData = new FormData();
  formData.append('organization', organization);
  formData.append('file_name', file.name || 'upload');
  if (file.type) {
    formData.append('content_type', file.type);
  }
  const res = await api.post(apiPath, formData, { headers });
  return res.data;
}

/**
 * Multipart form POST to S3 using presigned post policy (bypasses app CDN; browser talks to S3 only).
 * The `file` part must be last, per S3 form POST.
 * @param {string} postUrl
 * @param {Record<string, string>} formFields hidden fields from the presign response
 * @param {File} file
 * @returns {Promise<void>}
 */
export async function postFileToS3PresignedPost(postUrl, formFields, file) {
  const form = new FormData();
  Object.keys(formFields).forEach((k) => {
    form.append(k, formFields[k]);
  });
  form.append('file', file, file.name || 'upload');
  let res;
  try {
    res = await fetch(postUrl, {
      method: 'POST',
      body: form,
    });
  } catch (e) {
    const n = e?.name || '';
    const msg = String(e?.message || e || '');
    if (n === 'TypeError' || n === 'NetworkError' || /NetworkError|failed to fetch|load failed|fetch resource/i.test(msg)) {
      throw new Error(S3_BROWSER_BLOCK_ERROR);
    }
    throw e;
  }
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(
      `S3 upload failed (${res.status} ${res.statusText})${t ? `: ${t.slice(0, 300)}` : ''}`
    );
  }
}

const S3_CORS_OR_BLOCKED_MESSAGE =
  'The browser could not complete the direct upload to S3. Check CORS on *-cube-file-uploads: allow Methods POST, PUT, GET, HEAD, your exact page origin, and AllowedHeaders * (form POST needs OPTIONS preflight). Confirm the API AWS region matches the bucket. Or use the S3 path field if uploads must bypass the browser.';

/** Extra hint when browser → S3 PUT fails (CORS, bad signature, etc.). */
export function formatS3DirectUploadError(error) {
  if (!error) return null;
  const m = String(error.message || '');
  if (m === S3_BROWSER_BLOCK_ERROR) {
    return S3_CORS_OR_BLOCKED_MESSAGE;
  }
  if (m.startsWith('S3 upload failed') || m.includes('S3 upload failed (')) {
    return `${m} Check S3 CORS (POST to bucket), bucket policy, and IAM; or use the S3 path field.`;
  }
  // Raw fetch failures we did not re-wrap (e.g. older code paths)
  if (
    error.name === 'NetworkError' ||
    (error.name === 'TypeError' &&
      (m === 'Failed to fetch' || m.toLowerCase().includes('load failed'))) ||
    /NetworkError when attempting to fetch|failed to fetch/i.test(m)
  ) {
    return S3_CORS_OR_BLOCKED_MESSAGE;
  }
  return null;
}
